import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  FaProjectDiagram, 
  FaSignOutAlt, 
  FaUser, 
  FaHome,
  FaMoneyBillWave,
  FaChartBar,
  FaClipboardList,
  FaUsers,
  FaBell,
  FaCheckCircle,
  FaTimes,
  FaAngleDown,
  FaSearch,
  FaCog
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import apiService from '../../services/api';

// Styled Components
const LayoutContainer = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    "sidebar header"
    "sidebar main";
  min-height: 100vh;
  background: #f5f7fa;
  
  @media (max-width: 1024px) {
    grid-template-columns: 80px 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "header"
      "main";
  }
`;

const Header = styled.header`
  grid-area: header;
  background: white;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: #f5f7fa;
  border-radius: 50px;
  padding: 0.5rem 1rem;
  width: 300px;
  transition: all 0.3s ease;
  
  &:focus-within {
    box-shadow: 0 0 0 2px rgba(20, 19, 64, 0.1);
    background: white;
  }
  
  input {
    border: none;
    background: transparent;
    padding: 0.5rem;
    width: 100%;
    font-size: 0.9rem;
    
    &:focus {
      outline: none;
    }
  }
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const NotificationBell = styled.div`
  position: relative;
  cursor: pointer;
  font-size: 1.2rem;
  color: #141340;
  
  .count {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #F68B21;
    color: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }
`;

const NotificationsPanel = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 350px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(20, 19, 64, 0.15);
  padding: 1rem;
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-20px)'};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  
  @media (max-width: 768px) {
    width: 300px;
    right: -100px;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
  
  h4 {
    margin: 0;
    font-size: 1rem;
  }
  
  .actions {
    display: flex;
    gap: 0.5rem;
    font-size: 0.8rem;
    
    button {
      background: none;
      border: none;
      color: #F68B21;
      cursor: pointer;
      padding: 0;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const NotificationItem = styled.div<{ isRead: boolean }>`
  padding: 0.75rem;
  border-radius: 8px;
  background: ${props => props.isRead ? 'transparent' : 'rgba(246, 139, 33, 0.05)'};
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(246, 139, 33, 0.1);
  }
  
  .notification-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    
    .title {
      font-weight: ${props => props.isRead ? '500' : '600'};
      font-size: 0.9rem;
      color: #141340;
    }
    
    .time {
      font-size: 0.75rem;
      color: #6c757d;
    }
  }
  
  .notification-content {
    font-size: 0.85rem;
    color: #495057;
  }
  
  .notification-project {
    font-size: 0.75rem;
    color: #F68B21;
    margin-top: 0.25rem;
  }
`;

const UserProfile = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  position: relative;
  
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #F68B21 0%, #141340 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1rem;
  }
  
  .user-info {
    display: flex;
    flex-direction: column;
    
    .name {
      font-weight: 600;
      font-size: 0.9rem;
      color: #141340;
    }
    
    .role {
      font-size: 0.75rem;
      color: #6c757d;
    }
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
  
  .dropdown-icon {
    color: #6c757d;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 220px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(20, 19, 64, 0.15);
  padding: 1rem;
  transform: ${props => props.isOpen ? 'translateY(10px)' : 'translateY(-20px)'};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const UserDropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  color: #495057;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(246, 139, 33, 0.1);
    color: #F68B21;
  }
  
  &.logout {
    color: #dc3545;
    
    &:hover {
      background: rgba(220, 53, 69, 0.1);
      color: #dc3545;
    }
  }
`;

const Sidebar = styled.aside`
  grid-area: sidebar;
  background: #141340;
  color: white;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 200;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    position: fixed;
    left: -280px;
    width: 280px;
    
    &.open {
      left: 0;
    }
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .logo {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .title {
    font-size: 1.2rem;
    font-weight: 700;
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
`;

const SidebarFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  flex-shrink: 0;
  
  .footer-content {
    text-align: center;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.4;
  }
  
  .copyright {
    margin-bottom: 0.25rem;
  }
  
  .developer {
    font-size: 0.7rem;
    opacity: 0.8;
  }
  
  @media (max-width: 1024px) {
    .footer-content {
      font-size: 0.65rem;
    }
    
    .developer {
      font-size: 0.6rem;
    }
  }
`;

const NavGroup = styled.div`
  margin-bottom: 1.5rem;
  
  .group-title {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.5);
    padding: 0 1rem;
    margin-bottom: 0.75rem;
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

const NavItem = styled(Link)<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  color: ${props => props.isActive ? '#F68B21' : 'rgba(255, 255, 255, 0.8)'};
  text-decoration: none;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  transition: all 0.2s ease;
  background: ${props => props.isActive ? 'rgba(246, 139, 33, 0.1)' : 'transparent'};
  position: relative;
  
  .icon {
    font-size: 1.2rem;
    min-width: 24px;
    display: flex;
    justify-content: center;
  }
  
  .label {
    @media (max-width: 1024px) {
      display: none;
    }
  }
  
  .badge {
    position: absolute;
    right: 1rem;
    background: #F68B21;
    color: white;
    font-size: 0.7rem;
    font-weight: bold;
    padding: 0.1rem 0.5rem;
    border-radius: 10px;
    
    @media (max-width: 1024px) {
      right: 0;
      top: 0.5rem;
    }
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #F68B21;
  }
`;

const MainContent = styled.main`
  grid-area: main;
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: #141340;
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const SidebarOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 199;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ManagerLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'New Fund Request',
      content: 'John Doe submitted a new fund request for approval',
      project: 'Water Supply Project - Dar es Salaam',
      time: '10 minutes ago',
      isRead: false
    },
    {
      id: 2,
      title: 'Request Approved',
      content: 'Your fund request #FR-2024-042 was approved by MD',
      project: 'Road Construction - Arusha',
      time: '2 hours ago',
      isRead: false
    },
    {
      id: 3,
      title: 'Payment Completed',
      content: 'Fund request #FR-2024-039 has been paid by Accountant',
      project: 'Water Supply Project - Dar es Salaam',
      time: '1 day ago',
      isRead: true
    },
    {
      id: 4,
      title: 'New Staff Assigned',
      content: 'Grace Nyambo has been assigned to your project',
      project: 'Road Construction - Arusha',
      time: '2 days ago',
      isRead: true
    }
  ]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const unreadNotificationsCount = notifications.filter(n => !n.isRead).length;

  // Fetch pending approval count for fund requests
  useEffect(() => {
    const fetchPendingApprovalCount = async () => {
      if (!user || user.role !== 'Project Manager') return;
      
      try {
        // Get assigned projects for the current manager
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const managerProjects = assignedProjectsResponse.data || [];
        
        if (managerProjects.length > 0) {
          const managerProjectIds = managerProjects.map((project: any) => project._id || project.id);
          
          // Get fund requests for manager's projects
          const fundRequestsResponse = await apiService.getFundRequests();
          const allRequests = fundRequestsResponse.data || [];
          
          // Filter fund requests for manager's projects
          const managerRequests = allRequests.filter((request: any) => 
            managerProjectIds.includes(request.projectId)
          );
          
          // Count requests specifically pending Project Manager approval
          const pendingCount = managerRequests.filter((r: any) => {
            const pendingManagerStep = r.approvalChain?.find((step: any) => 
              step.role === 'Project Manager' && step.status === 'Pending'
            );
            return pendingManagerStep !== undefined;
          }).length;
          
          setPendingApprovalCount(pendingCount);
        } else {
          setPendingApprovalCount(0);
        }
      } catch (error) {
        console.error('Error fetching pending approval count:', error);
        setPendingApprovalCount(0);
      }
    };

    fetchPendingApprovalCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingApprovalCount, 30000);
    
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  const markNotificationAsRead = (id: number) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, isRead: true } : n
    ));
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
    if (notificationsOpen) setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    if (userDropdownOpen) setUserDropdownOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-profile-container') && userDropdownOpen) {
        setUserDropdownOpen(false);
      }
      if (!target.closest('.notification-container') && notificationsOpen) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen, notificationsOpen]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const nameParts = user.name.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };

  return (
    <LayoutContainer>
      <Sidebar className={sidebarOpen ? 'open' : ''}>
        <SidebarHeader>
          <div className="logo">
            <img src="/pfrs.jpeg" alt="PFRS Logo" />
          </div>
          <div className="title">PFRS</div>
        </SidebarHeader>
        
        <SidebarNav>
          <NavGroup>
            <div className="group-title">Main</div>
            <NavItem to="/manager" isActive={isActiveRoute('/manager')}>
              <div className="icon"><IconWrapper icon={FaHome} /></div>
              <span className="label">Dashboard</span>
            </NavItem>
            <NavItem to="/manager/projects" isActive={isActiveRoute('/manager/projects') || location.pathname.includes('/manager/projects/')}>
              <div className="icon"><IconWrapper icon={FaProjectDiagram} /></div>
              <span className="label">Projects</span>
            </NavItem>
            <NavItem to="/manager/fund-requests" isActive={isActiveRoute('/manager/fund-requests') || location.pathname.includes('/manager/fund-requests/')}>
              <div className="icon"><IconWrapper icon={FaMoneyBillWave} /></div>
              <span className="label">Fund Requests</span>
              {pendingApprovalCount > 0 && <span className="badge">{pendingApprovalCount}</span>}
            </NavItem>
          </NavGroup>
          
          <NavGroup>
            <div className="group-title">Reports</div>
            <NavItem to="/manager/reports" isActive={isActiveRoute('/manager/reports')}>
              <div className="icon"><IconWrapper icon={FaChartBar} /></div>
              <span className="label">Reports</span>
            </NavItem>
            <NavItem to="/manager/team" isActive={isActiveRoute('/manager/team')}>
              <div className="icon"><IconWrapper icon={FaUsers} /></div>
              <span className="label">Team</span>
            </NavItem>
          </NavGroup>
        </SidebarNav>
        
        <SidebarFooter>
          <div className="footer-content">
            <div className="copyright">
              © 2024 PFRS. All rights reserved.
            </div>
            <div className="developer">
              Developed by Sandfish
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>
      
      <SidebarOverlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <MobileMenuToggle onClick={toggleSidebar}>
            <IconWrapper icon={FaProjectDiagram} />
          </MobileMenuToggle>
          
          <SearchBar>
            <IconWrapper icon={FaSearch} style={{ color: '#6c757d' }} />
            <input type="text" placeholder="Search..." />
          </SearchBar>
        </div>
        
        <UserControls>
          <div className="notification-container" style={{ position: 'relative' }}>
            <NotificationBell onClick={toggleNotifications}>
              <IconWrapper icon={FaBell} />
              {unreadNotificationsCount > 0 && <span className="count">{unreadNotificationsCount}</span>}
            </NotificationBell>
            
            <NotificationsPanel isOpen={notificationsOpen}>
              <NotificationHeader>
                <h4>Notifications</h4>
                <div className="actions">
                  <button onClick={markAllAsRead}>Mark all as read</button>
                </div>
              </NotificationHeader>
              
              {notifications.length > 0 ? (
                notifications.map(notification => (
                  <NotificationItem 
                    key={notification.id} 
                    isRead={notification.isRead}
                    onClick={() => markNotificationAsRead(notification.id)}
                  >
                    <div className="notification-header">
                      <div className="title">{notification.title}</div>
                      <div className="time">{notification.time}</div>
                    </div>
                    <div className="notification-content">{notification.content}</div>
                    <div className="notification-project">{notification.project}</div>
                  </NotificationItem>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem 0', color: '#6c757d' }}>
                  No notifications
                </div>
              )}
            </NotificationsPanel>
          </div>
          
          <div className="user-profile-container" style={{ position: 'relative' }}>
            <UserProfile isOpen={userDropdownOpen} onClick={toggleUserDropdown}>
              <div className="avatar">{getUserInitials()}</div>
              <div className="user-info">
                <div className="name">{user?.name}</div>
                <div className="role">{user?.role}</div>
              </div>
              <IconWrapper icon={FaAngleDown} className="dropdown-icon" />
            </UserProfile>
            
            <UserDropdown isOpen={userDropdownOpen}>
              <UserDropdownItem to="/manager/profile">
                <IconWrapper icon={FaUser} />
                Profile
              </UserDropdownItem>
              <UserDropdownItem to="/manager/settings">
                <IconWrapper icon={FaCog} />
                Settings
              </UserDropdownItem>
              <hr style={{ margin: '0.5rem 0', opacity: 0.2 }} />
              <UserDropdownItem to="#" className="logout" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                <IconWrapper icon={FaSignOutAlt} />
                Logout
              </UserDropdownItem>
            </UserDropdown>
          </div>
        </UserControls>
      </Header>
      
      <MainContent>
        <Outlet />
      </MainContent>
    </LayoutContainer>
  );
};

export default ManagerLayout; 